import type { FeatureEvents, ScreenEvent, TrackEvent, UIEvent } from '../new-analytics-types';
import { Action, ActionSubject, TrackAction, TrackActionSubject } from '../new-analytics-types';

type ScreenKeys =
  | 'conditionalAccessPoliciesScreenEvent'
  | 'conditionalAccessPolicyDetailsScreenEvent'
  | 'addConditionalAccessPolicyModalScreenEvent'
  | 'activateConditionalAccessPolicyModalScreenEvent'
  | 'deactivateConditionalAccessPolicyModalScreenEvent'
  | 'deleteConditionalAccessPolicyModalScreenEvent';

type UIEventKeys =
  | 'createConditionalAccessPolicyButtonEvent'
  | 'confirmCreateConditionalAccessPolicyEvent'
  | 'cancelCreateConditionalAccessPolicyEvent'
  | 'editConditionalAccessPolicyEvent'
  | 'deleteConditionalAccessPolicyDropdownItemEvent'
  | 'confirmDeleteConditionalAccessPolicyEvent'
  | 'cancelDeleteConditionalAccessPolicyEvent'
  | 'activateConditionalAccessPolicyButtonEvent'
  | 'confirmActivateConditionalAccessPolicyEvent'
  | 'cancelActivateConditionalAccessPolicyEvent'
  | 'deactivateConditionalAccessPolicyButtonEvent'
  | 'confirmDeactivateConditionalAccessPolicyEvent'
  | 'cancelDeactivateConditionalAccessPolicyEvent'
  | 'updateConditionalAccessPolicyEvent'
  | 'saveConditionalAccessPolicyEvent'
  | 'confirmConditionalAccessPolicyNameEditEvent';

type TrackEventKeys =
  | 'createdConditionalAccessPolicyTrackEvent'
  | 'updatedConditionalAccessPolicyTrackEvent'
  | 'activatedConditionalAccessPolicyTrackEvent'
  | 'deactivatedConditionalAccessPolicyTrackEvent'
  | 'deletedConditionalAccessPolicyTrackEvent'
  | 'renamedConditionalAccessPolicyTrackEvent';

export enum ConditionalAccessPolicyScreens {
  conditionalAccessPoliciesScreen = 'conditionalAccessPoliciesScreen',
  conditionalAccessPolicyDetailsScreen = 'conditionalAccessPolicyDetailsScreen',
  addConditionalAccessPolicyModal = 'addConditionalAccessPolicyModal',
  activateConditionalAccessPolicyModal = 'activateConditionalAccessPolicyModal',
  deactivateConditionalAccessPolicyModal = 'deactivateConditionalAccessPolicyModal',
  deleteConditionalAccessPolicyModal = 'deleteConditionalAccessPolicyModal',
}

export const conditionalAccessPolicyEvents: FeatureEvents<ScreenKeys, UIEventKeys, TrackEventKeys, string> = {
  confluenceSpec: 'https://hello.atlassian.net/wiki/spaces/caai/pages/6677071702/CAP+M1+Frontend+Analytics+Spec',
  screen: {
    conditionalAccessPoliciesScreenEvent: (): ScreenEvent => ({
      data: {
        name: ConditionalAccessPolicyScreens.conditionalAccessPoliciesScreen,
      },
    }),
    conditionalAccessPolicyDetailsScreenEvent: (attributes: { policyId: string; isPolicyActive: boolean }): ScreenEvent => ({
      data: {
        name: ConditionalAccessPolicyScreens.conditionalAccessPolicyDetailsScreen,
        attributes,
      },
    }),
    addConditionalAccessPolicyModalScreenEvent: (): ScreenEvent => ({
      data: {
        name: ConditionalAccessPolicyScreens.addConditionalAccessPolicyModal,
      },
    }),
    activateConditionalAccessPolicyModalScreenEvent: (attributes: { policyId: string }): ScreenEvent => ({
      data: {
        name: ConditionalAccessPolicyScreens.activateConditionalAccessPolicyModal,
        attributes,
      },
    }),
    deactivateConditionalAccessPolicyModalScreenEvent: (attributes: { policyId: string }): ScreenEvent => ({
      data: {
        name: ConditionalAccessPolicyScreens.deactivateConditionalAccessPolicyModal,
        attributes,
      },
    }),
    deleteConditionalAccessPolicyModalScreenEvent: (attributes: { policyId: string; isPolicyActive: boolean }): ScreenEvent => ({
      data: {
        name: ConditionalAccessPolicyScreens.deleteConditionalAccessPolicyModal,
        attributes,
      },
    }),
  },
  ui: {
    createConditionalAccessPolicyButtonEvent: (): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'createConditionalAccessPolicyButton',
        source: ConditionalAccessPolicyScreens.conditionalAccessPoliciesScreen,
      },
    }),
    confirmCreateConditionalAccessPolicyEvent: (attributes: { policyName: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'confirmCreateConditionalAccessPolicy',
        source: ConditionalAccessPolicyScreens.addConditionalAccessPolicyModal,
        attributes,
      },
    }),
    cancelCreateConditionalAccessPolicyEvent: (): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'cancelCreateConditionalAccessPolicy',
        source: ConditionalAccessPolicyScreens.addConditionalAccessPolicyModal,
      },
    }),
    editConditionalAccessPolicyEvent: (attributes: { policyId: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.DropdownItem,
        actionSubjectId: 'editConditionalAccessPolicy',
        source: ConditionalAccessPolicyScreens.conditionalAccessPoliciesScreen,
        attributes,
      },
    }),
    deleteConditionalAccessPolicyDropdownItemEvent: (attributes: { policyId: string; isPolicyActive: boolean }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.DropdownItem,
        actionSubjectId: 'deleteConditionalAccessPolicyDropdownItem',
        source: ConditionalAccessPolicyScreens.conditionalAccessPoliciesScreen,
        attributes,
      },
    }),
    confirmDeleteConditionalAccessPolicyEvent: (attributes: { policyId: string; isPolicyActive: boolean }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'confirmDeleteConditionalAccessPolicy',
        source: ConditionalAccessPolicyScreens.deleteConditionalAccessPolicyModal,
        attributes,
      },
    }),
    cancelDeleteConditionalAccessPolicyEvent: (attributes: { policyId: string; isPolicyActive: boolean }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'cancelDeleteConditionalAccessPolicy',
        source: ConditionalAccessPolicyScreens.deleteConditionalAccessPolicyModal,
        attributes,
      },
    }),
    activateConditionalAccessPolicyButtonEvent: (attributes: { policyId: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'activateConditionalAccessPolicyButton',
        source: ConditionalAccessPolicyScreens.conditionalAccessPolicyDetailsScreen,
        attributes,
      },
    }),
    confirmActivateConditionalAccessPolicyEvent: (attributes: { policyId: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'confirmActivateConditionalAccessPolicy',
        source: ConditionalAccessPolicyScreens.activateConditionalAccessPolicyModal,
        attributes,
      },
    }),
    cancelActivateConditionalAccessPolicyEvent: (attributes: { policyId: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'cancelActivateConditionalAccessPolicy',
        source: ConditionalAccessPolicyScreens.activateConditionalAccessPolicyModal,
        attributes,
      },
    }),
    deactivateConditionalAccessPolicyButtonEvent: (attributes: { policyId: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'deactivateConditionalAccessPolicyButton',
        source: ConditionalAccessPolicyScreens.conditionalAccessPolicyDetailsScreen,
        attributes,
      },
    }),
    confirmDeactivateConditionalAccessPolicyEvent: (attributes: { policyId: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'confirmDeactivateConditionalAccessPolicy',
        source: ConditionalAccessPolicyScreens.deactivateConditionalAccessPolicyModal,
        attributes,
      },
    }),
    cancelDeactivateConditionalAccessPolicyEvent: (attributes: { policyId: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'cancelDeactivateConditionalAccessPolicy',
        source: ConditionalAccessPolicyScreens.deactivateConditionalAccessPolicyModal,
        attributes,
      },
    }),
    updateConditionalAccessPolicyEvent: (attributes: { policyId: string; isPolicyActive: boolean }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'updateConditionalAccessPolicy',
        source: ConditionalAccessPolicyScreens.conditionalAccessPolicyDetailsScreen,
        attributes,
      },
    }),
    saveConditionalAccessPolicyEvent: (attributes: { policyId: string; isPolicyActive: boolean }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'saveConditionalAccessPolicy',
        source: ConditionalAccessPolicyScreens.conditionalAccessPolicyDetailsScreen,
        attributes,
      },
    }),
    confirmConditionalAccessPolicyNameEditEvent: (attributes: { policyId: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'confirmConditionalAccessPolicyNameEdit',
        source: ConditionalAccessPolicyScreens.conditionalAccessPolicyDetailsScreen,
        attributes,
      },
    }),
  },
  track: {
    createdConditionalAccessPolicyTrackEvent: (attributes: { policyId: string; policyName: string }): TrackEvent => ({
      data: {
        action: TrackAction.Created,
        actionSubject: TrackActionSubject.ConditionalAccessPolicy,
        source: ConditionalAccessPolicyScreens.addConditionalAccessPolicyModal,
        attributes,
      },
    }),
    updatedConditionalAccessPolicyTrackEvent: (attributes: {
      policyId: string;
      isPolicyActive: boolean;
      accessControl: string;
    }): TrackEvent => ({
      data: {
        action: TrackAction.Updated,
        actionSubject: TrackActionSubject.ConditionalAccessPolicy,
        source: ConditionalAccessPolicyScreens.conditionalAccessPolicyDetailsScreen,
        attributes,
      },
    }),
    activatedConditionalAccessPolicyTrackEvent: (attributes: { policyId: string }): TrackEvent => ({
      data: {
        action: TrackAction.Activated,
        actionSubject: TrackActionSubject.ConditionalAccessPolicy,
        source: ConditionalAccessPolicyScreens.activateConditionalAccessPolicyModal,
        attributes,
      },
    }),
    deactivatedConditionalAccessPolicyTrackEvent: (attributes: { policyId: string }): TrackEvent => ({
      data: {
        action: TrackAction.Deactivated,
        actionSubject: TrackActionSubject.ConditionalAccessPolicy,
        source: ConditionalAccessPolicyScreens.deactivateConditionalAccessPolicyModal,
        attributes,
      },
    }),
    deletedConditionalAccessPolicyTrackEvent: (attributes: { policyId: string; isPolicyActive: boolean }): TrackEvent => ({
      data: {
        action: TrackAction.Deleted,
        actionSubject: TrackActionSubject.ConditionalAccessPolicy,
        source: ConditionalAccessPolicyScreens.deleteConditionalAccessPolicyModal,
        attributes,
      },
    }),
    renamedConditionalAccessPolicyTrackEvent: (attributes: { policyId: string }): TrackEvent => ({
      data: {
        action: TrackAction.Renamed,
        actionSubject: TrackActionSubject.ConditionalAccessPolicy,
        source: ConditionalAccessPolicyScreens.conditionalAccessPolicyDetailsScreen,
        attributes,
      },
    }),
  },
  operational: {},
};
